import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { permissionService } from "../services/api";
import {
  PERMISSION_TYPE_INVOICE_BACKDATE,
  resolveApprovedPermissionsList,
  resolvePendingRequestsList,
} from "../utils/inwardBackdatePermission";

export function useInwardBackdatePermission() {
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedPermissions, setApprovedPermissions] = useState([]);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const applyStatusPayload = useCallback((data) => {
    const pending = resolvePendingRequestsList({
      pendingRequests: data?.pendingRequests,
      pendingRequest: data?.pendingRequest,
    });
    const approved = resolveApprovedPermissionsList({
      approvedPermissions: data?.approvedPermissions,
      activePermission: data?.activePermission,
    });

    setPendingRequests(pending);
    setApprovedPermissions(approved);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await permissionService.getStatus({
        permissionType: PERMISSION_TYPE_INVOICE_BACKDATE,
      });
      if (!mountedRef.current) return;
      if (res?.success) {
        applyStatusPayload(res.data);
        setError(null);
      } else {
        setError(res?.error || "Failed to load permission status");
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || "Failed to load permission status");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [applyStatusPayload]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  // LEGACY: refresh when 24-hour access expired.
  // useEffect(() => {
  //   if (!activePermission?.expiresAt) return undefined;
  //   const expiresMs = new Date(activePermission.expiresAt).getTime() - Date.now();
  //   if (expiresMs <= 0) {
  //     refresh();
  //     return undefined;
  //   }
  //   const timer = setTimeout(() => refresh(), expiresMs + 300);
  //   return () => clearTimeout(timer);
  // }, [activePermission?.expiresAt, refresh]);

  useEffect(() => {
    if (!pendingRequests.length && !approvedPermissions.length) return undefined;
    const interval = setInterval(() => refresh(), 60 * 1000);
    return () => clearInterval(interval);
  }, [pendingRequests.length, approvedPermissions.length, refresh]);

  const pendingRequest = pendingRequests[0] || null;
  const activePermission = approvedPermissions[0] || null;

  const permissionState = useMemo(
    () => ({
      pendingRequests,
      approvedPermissions,
      pendingRequest,
      activePermission,
    }),
    [pendingRequests, approvedPermissions, pendingRequest, activePermission],
  );

  return {
    loading,
    error,
    pendingRequests,
    approvedPermissions,
    pendingRequest,
    activePermission,
    permissionState,
    refresh,
    hasPendingRequest: pendingRequests.length > 0,
    hasActiveAccess: approvedPermissions.length > 0,
  };
}
